import { createApi } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import Cookies from "js-cookie";
import getTenantId from "../../components/Tenent";
import { showAccessDeniedModal, showSessionExpiredModal } from "../slices/authSlice";
const getStore = () => require("../store").store;

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_ACTIVITIES_HOST_URL,
  withCredentials: true,
  headers: {
    accept: `application/json`,
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    config.headers["x-tenant-id"] = getTenantId();
    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401 && window.location.pathname !== "/login") {
      getStore().dispatch(showSessionExpiredModal(window.location.pathname));
    }
    return Promise.reject(err);
  },
);

const axiosBaseQuery =
  () =>
  async ({ responseType, url, method, body, params, ...requestOpts }) => {
    try {
      const blobEndpoints = ["pcf/generate_pcf_report", "cbam/generate_report"];
      const isBlobRequest = blobEndpoints.some((endpoint) =>
        url.includes(endpoint)
      );
      const axiosOptions = {
        url: url,
        method,
        data: body,
        params,
        headers: requestOpts.headers,
        responseType: isBlobRequest ? "blob" : "json",
      };

      const result = await axiosInstance(axiosOptions);
      return { data: result.data };
    } catch (axiosError) {
      let err = axiosError;
      const errorData = axiosError.response?.data;
      const status = axiosError.response?.status;

      if (status === 403 && errorData?.error === "forbidden") {
        getStore().dispatch(showAccessDeniedModal());
      }
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };

export const assurancemodule = createApi({
  reducerPath: "assurancemodule",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["assurancemodule"],
  endpoints: (builder) => ({
    getAssuranceList: builder.query({
      query: ({ time_period_start, time_period_end }) => ({
        url: `assurance/get_list`,
        method: "GET",
        params: {
          time_period_start,
          time_period_end,
        },
      }),
      providesTags: ["assurancemodule"],
    }),
    getFeedbackResults: builder.query({
      query: () => ({
        url: "assurance/get_feedback_results",
        method: "GET",
      }),
    }),

    submitVerificationPercentages: builder.mutation({
      query: (data) => ({
        url: "assurance/set_percentage",
        method: "POST",

        body: data,
      }),
    }),
    getFeedbackData: builder.query({
      query: ({
        page = 1,
        limit = 10,
        activity,
        category,
        mapped_count,
        count_sort,
      }) => ({
        url: "assurance/get_feedback_data",
        method: "GET",
        params: {
          page,
          limit,
          activity,
          category,
          mapped_count,
          count_sort,
        },
      }),
    }),
    updateFeedbackStatus: builder.mutation({
      query: (payload) => ({
        url: "assurance/update_feedback_data",
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: ["assurancemodule"],
    }),
    getStatus: builder.query({
      query: () => ({
        url: "assurance/check_session",
      }),
    }),
    getMappedData: builder.query({
      query: ({ category }) => ({
        url: "assurance/get_entries_count",
        method: "GET",
        params: {
          category,
        },
      }),
    }),
    // Scheduler Endpoints
    addScheduler: builder.mutation({
      query: (body) => ({
        url: "emp_com/add_node",
        method: "POST",
        body,
      }),
      invalidatesTags: ["scheduler"],
    }),
    updateScheduler: builder.mutation({
      query: (body) => ({
        url: "emp_com/edit_node",
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["scheduler"],
    }),
    getSchedules: builder.query({
      query: ({ id }) => ({
        url: `emp_com/fetch_nodes?category_id=${id}`,
      }),
      providesTags: ["scheduler"],
    }),
    getActivities: builder.query({
      query: ({ cate }) => ({
        url: `/fetch_category_activities?category=${cate}`,
      }),
    }),

    getEmissionsHistory: builder.query({
      query: ({ node_id, id }) => ({
        url: `emp_com/fetch_emissions?node_id=${node_id}&category_id=${id}`,
      }),
      providesTags: ["emp-group"],
    }),
    getEmissions: builder.query({
      query: ({ node_id, id }) => ({
        url: `emp_com/fetch_sources?node_id=${node_id}&category_id=${id}`,
      }),
      providesTags: ["emp-group"],
    }),
    addEmployeeGroup: builder.mutation({
      query: (body) => ({
        url: `emp_com/add_employee_group`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["emp-group"],
    }),
    editEmployeeGroup: builder.mutation({
      query: (body) => ({
        url: `emp_com/edit_employee_group`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["emp-group"],
    }),
    deleteEmployeeGroup: builder.mutation({
      query: ({ node_id, category_id, employee_type }) => ({
        url: `emp_com/delete_employee_group`,
        method: "DELETE",
        params: { node_id, category_id, employee_type },
      }),
      invalidatesTags: ["emp-group"],
    }),
    getEmissionsSummary: builder.query({
      // ⬇️ use query params instead of string concat
      query: ({ level_name, year }) => ({
        url: `/emissions/summary`,
        params: { level_name, year },
      }),
    }),
    getAssuranceReports: builder.query({
      query: () => ({
        url: `/assurance/get_assurance_reports`,
      }),
    }),
    getAssuranceLogs: builder.query({
      query: () => ({
        url: `/assurance/fetch_requests`,
      }),
      providesTags: ['changeRequest']
    }),
    createCorrectionRequest: builder.mutation({
      query: (body) => ({
        url: `/assurance/request_correction`,
        method: "POST",
        body,
      }),
      invalidatesTags: ['changeRequest']
    }),
    getAssuranceReportData: builder.query({
      query: (body) => ({
        url: `/assurance/get_assurance_report_data`,
        params: body,
      }),
    }),
    getEmissionsReport: builder.query({
      query: ({node_id, fy_year}) => ({
        url: `/fy_based_emissions`,
        params: {
          fy_year,
          node_id
        }
      })
    }),
    getReport: builder.query({
      query: ({fy_year}) => ({
        url: `/get_report`,
        params: {
          fy_year
        }
      })
    }),
    submitReport: builder.mutation({
      query: (payload) => ({
        url: `/post_report`,
        method: 'POST',
        body: payload
      })
    })
  }),
});

export const {
  useGetAssuranceListQuery,
  useGetFeedbackDataQuery,
  useUpdateFeedbackStatusMutation,
  useGetFeedbackResultsQuery,
  useSubmitVerificationPercentagesMutation,
  useGetStatusQuery,
  useGetMappedDataQuery,
  useGetEmissionsSummaryQuery,
  useAddSchedulerMutation,
  useUpdateSchedulerMutation,
  useGetSchedulesQuery,
  useGetEmissionsHistoryQuery,
  useGetEmissionsQuery,
  useAddEmployeeGroupMutation,
  useEditEmployeeGroupMutation,
  useGetActivitiesQuery,
  useDeleteEmployeeGroupMutation,
  useGetAssuranceReportsQuery,
  useGetAssuranceLogsQuery,
  useCreateCorrectionRequestMutation,
  useGetAssuranceReportDataQuery,
  useGetEmissionsReportQuery,
  useGetReportQuery,
  useSubmitReportMutation
} = assurancemodule;
